// Hook que devuelve los planes activos del paciente con sus
// plan_ejercicios y nombre del ejercicio resuelto. Usado por
// DetallePaciente tab "Rutinas" para mostrar al kine qué le prescribió
// al paciente.
//
// "Activo" = plan con `estado = 'activo'` perteneciente a un tratamiento
// del paciente. Si el paciente tiene N tratamientos activos
// simultáneamente (caso raro: hombro + rodilla), trae los planes de
// todos. La UI puede agrupar por tratamiento si llega volumen real.
//
// Devuelve los plan_ejercicios ordenados por `orden` ASC dentro de cada
// plan, y los planes ordenados por `fecha_inicio` DESC (más reciente
// primero).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../client";

export interface PlanEjercicioActivo {
  id: string;
  ejercicio_id: string;
  ejercicio_nombre: string;
  orden: number;
  reps_objetivo: number;
  series_objetivo: number;
  descanso_seg: number | null;
  frecuencia_semanal: number | null;
  notas_kine: string | null;
}

export type TratamientoEstado =
  | "activo"
  | "completado"
  | "abandonado"
  | "pausado";

export interface PlanActivo {
  id: string;
  nombre: string;
  fecha_inicio: string;
  /** ISO date o null si plan abierto sin fecha de cierre. */
  fecha_fin: string | null;
  notas_kine: string | null;
  /** Estado del tratamiento al que pertenece el plan. Necesario para decidir
   *  si el kine puede editar/eliminar plan_ejercicios (defense in depth — RPC
   *  también lo enforce con TRATAMIENTO_CERRADO). Sprint 3 B4a. */
  tratamientoEstado: TratamientoEstado;
  ejercicios: PlanEjercicioActivo[];
}

interface State {
  data: PlanActivo[] | null;
  error: string | null;
  loading: boolean;
}

export interface UsePlanesActivosReturn extends State {
  /** Re-corre el fetch. Útil después de delete/edit que requieran que
   *  el row desaparezca o re-aparezca con valores frescos. */
  refetch: () => Promise<void>;
}

interface PlanRowRaw {
  id: string;
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string | null;
  notas_kine: string | null;
  tratamientos: { paciente_id: string; estado: TratamientoEstado } | null;
  plan_ejercicios: Array<{
    id: string;
    ejercicio_id: string;
    orden: number;
    reps_objetivo: number;
    series_objetivo: number;
    descanso_seg: number | null;
    frecuencia_semanal: number | null;
    notas_kine: string | null;
    ejercicios: { nombre: string } | null;
  }>;
}

export function usePlanesActivos(
  pacienteId: string | undefined,
): UsePlanesActivosReturn {
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: !!pacienteId,
  });

  const fetchAll = useCallback(
    async (signal?: { cancelled: boolean }) => {
      if (!pacienteId) return;
      const { data, error } = await supabase
        .from("planes")
        .select(
          `
          id,
          nombre,
          fecha_inicio,
          fecha_fin,
          notas_kine,
          tratamientos!inner(paciente_id, estado),
          plan_ejercicios(
            id,
            ejercicio_id,
            orden,
            reps_objetivo,
            series_objetivo,
            descanso_seg,
            frecuencia_semanal,
            notas_kine,
            ejercicios!inner(nombre)
          )
        `,
        )
        .eq("estado", "activo")
        .eq("tratamientos.paciente_id", pacienteId)
        .order("fecha_inicio", { ascending: false })
        .returns<PlanRowRaw[]>();

      if (signal?.cancelled) return;

      if (error) {
        console.warn("[usePlanesActivos] fetch error", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      const planes: PlanActivo[] = (data ?? []).map((row) => ({
        id: row.id,
        nombre: row.nombre,
        fecha_inicio: row.fecha_inicio,
        fecha_fin: row.fecha_fin,
        notas_kine: row.notas_kine,
        tratamientoEstado:
          (row.tratamientos?.estado ?? "activo") as TratamientoEstado,
        ejercicios: row.plan_ejercicios
          .map((pe) => ({
            id: pe.id,
            ejercicio_id: pe.ejercicio_id,
            ejercicio_nombre: pe.ejercicios?.nombre ?? pe.ejercicio_id,
            orden: pe.orden,
            reps_objetivo: pe.reps_objetivo,
            series_objetivo: pe.series_objetivo,
            descanso_seg: pe.descanso_seg,
            frecuencia_semanal: pe.frecuencia_semanal,
            notas_kine: pe.notas_kine,
          }))
          .sort((a, b) => a.orden - b.orden),
      }));

      setState({ data: planes, error: null, loading: false });
    },
    [pacienteId],
  );

  useEffect(() => {
    if (!pacienteId) {
      setState({ data: null, error: null, loading: false });
      return;
    }
    const signal = { cancelled: false };
    setState({ data: null, error: null, loading: true });
    void fetchAll(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [pacienteId, fetchAll]);

  const refetch = useCallback(async () => {
    if (!pacienteId) return;
    await fetchAll();
  }, [pacienteId, fetchAll]);

  return { ...state, refetch };
}
